import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { TECH_PARTNERS } from "@/lib/data";

// Some brand marks are drawn smaller within their own artwork than others,
// so they read visually smaller even at the same box size — nudge those up.
const LOGO_SCALE = {
    gcp: 1.25,
    isc2: 1.3,
    istqb: 1.3,
};

const PartnerTile = ({ partner, idx }) => {
    const scale = LOGO_SCALE[partner.slug] || 1;
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.4, delay: (idx % 12) * 0.03 }}
        >
            <Link
                to={partner.slug ? `/partners/${partner.slug}` : "/partners"}
                className="group aspect-[3/2] sxe-card flex items-center justify-center p-4"
                style={{ background: "#FFFFFF" }}
                data-testid={`partner-tile-${idx}`}
                title={partner.name}
            >
                {partner.logo ? (
                    <img
                        src={partner.logo}
                        alt={partner.name}
                        loading="lazy"
                        className="h-14 w-[85%] object-contain opacity-80 group-hover:opacity-100 transition-opacity"
                        style={{
                            ...(partner.mono ? { filter: "brightness(0) invert(1)" } : null),
                            transform: scale !== 1 ? `scale(${scale})` : undefined,
                        }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                ) : (
                    <span className="font-display font-semibold tracking-[-0.02em] text-white/80 group-hover:text-white transition-colors text-center text-[15px] leading-tight">
                        {partner.name}
                    </span>
                )}
            </Link>
        </motion.div>
    );
};

const TechPartners = () => (
    <section data-testid="partners-section" className="sxe-section relative">
        <div className="sxe-container">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div className="max-w-2xl">
                    <div className="overline"><span className="dot" /> Technology Partners</div>
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.05] text-white mt-5">
                        18+ global partners.{" "}
                        <span className="text-gradient">One certified stack.</span>
                    </h2>
                    <p className="text-[#B0A3C4] text-lg leading-relaxed mt-5">
                        SpringXE&apos;s programs and certifications are aligned with the world&apos;s leading technology and standards bodies — so your teams learn on the platforms they&apos;ll actually use.
                    </p>
                </div>
                <Link to="/partners" data-testid="partners-explore-all-btn" className="btn-secondary shrink-0">
                    Explore All Partners <ArrowUpRight className="w-4 h-4" />
                </Link>
            </div>

            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-3">
                {TECH_PARTNERS.map((p, i) => (
                    <PartnerTile key={p.name} partner={p} idx={i} />
                ))}
            </div>
        </div>
    </section>
);

export default TechPartners;
